import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Loader2, LogOut, ShieldCheck } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import BookingForm from "@/components/landing/BookingForm";
import { Reveal } from "@/components/landing/Reveal";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DEFAULT_CONTENT } from "@/lib/defaultContent";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";

const STATUS_STYLE = {
  ny: "bg-mint/15 text-mint",
  kontaktad: "bg-bone/10 text-bone",
  klar: "bg-moss/30 text-bone/70",
};

function LeadRow({ lead, t, lang }) {
  return (
    <article data-testid={`my-lead-${lead.id}`} className="rounded-2xl border border-white/10 bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
            {lead.created_at ? new Date(lead.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "sv-SE") : ""}
          </p>
          <p className="mt-1 font-display text-base font-bold text-bone">
            {t("account.package")}: <span className="text-mint">{lead.package || "–"}</span>
          </p>
        </div>
        <span
          data-testid={`my-lead-status-${lead.id}`}
          className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLE[lead.status] || STATUS_STYLE.ny}`}
        >
          {t(`account.status.${lead.status}`)}
        </span>
      </div>
      {lead.message && <p className="mt-3 text-sm leading-relaxed text-fog">{lead.message}</p>}
    </article>
  );
}

export default function Account() {
  const { user, isAdmin, logout } = useAuth();
  const { t, lang } = useLang();
  useSeo({ title: `${t("account.tag")} – Nivå Digital`, description: t("account.sub"), noindex: true });
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [packages, setPackages] = useState(DEFAULT_CONTENT.packages);

  useEffect(() => {
    if (user === null) navigate("/logga-in?next=/mitt-konto", { replace: true });
  }, [user, navigate]);

  const loadLeads = () => api.get("/my/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));

  useEffect(() => {
    if (!user) return;
    loadLeads();
    api.get("/content").then((r) => r.data?.packages && setPackages(r.data.packages)).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="h-8 w-8 animate-spin text-mint" />
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <main data-testid="account-page" className="min-h-screen bg-ink font-body text-bone">
      <PageHeader wide />
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-mint">{t("account.tag")}</p>
              <h1 data-testid="account-greeting" className="mt-4 font-display text-3xl font-black tracking-tight sm:text-4xl">
                {t("account.hello")}, {user.name?.split(" ")[0] || user.email}.
              </h1>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-fog">{t("account.sub")}</p>
            </div>
            <button
              data-testid="account-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-fog transition-colors hover:border-mint hover:text-mint"
            >
              <LogOut className="h-4 w-4" /> {t("nav.logout")}
            </button>
          </div>
        </Reveal>

        {isAdmin && (
          <Reveal delay={0.08}>
            <Link
              data-testid="account-admin-card"
              to="/admin"
              className="group mt-10 flex items-center justify-between gap-4 rounded-2xl border border-mint/40 bg-forest/30 p-6 transition-colors hover:border-mint"
            >
              <div className="flex items-center gap-4">
                <ShieldCheck className="h-6 w-6 text-mint" />
                <div>
                  <p className="font-display text-base font-bold text-bone">{t("account.adminCard")}</p>
                  <p className="text-sm text-fog">{t("account.adminBtn")}</p>
                </div>
              </div>
              <ArrowUpRight className="h-5 w-5 text-mint transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        )}

        <div className="mt-14 grid gap-12 lg:grid-cols-2">
          <Reveal delay={0.12}>
            <h2 className="font-display text-xl font-bold text-bone">{t("account.enquiries")}</h2>
            <div className="mt-6 space-y-4" data-testid="my-leads-list">
              {leads === null && <Loader2 className="h-6 w-6 animate-spin text-mint" />}
              {leads?.length === 0 && (
                <p data-testid="my-leads-empty" className="rounded-2xl border border-dashed border-white/15 p-8 text-center text-sm text-fog">
                  {t("account.empty")}
                </p>
              )}
              {leads?.map((lead) => <LeadRow key={lead.id} lead={lead} t={t} lang={lang} />)}
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <h2 className="font-display text-xl font-bold text-bone">{t("account.new")}</h2>
            <div className="mt-6 rounded-2xl border border-white/10 bg-surface p-6 sm:p-8">
              <BookingForm packages={packages} onSent={loadLeads} hideTitle />
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
